<template>
  <div
    class="comparison-module"
    :class="[themeName, currentRoute]"
  >
    <TransitionNavbar
      @afterEnter="showAddressBar = true"
    >
      <Navbar
        v-if="showNavbar"
        @logoClick="logoClick"
      >
        <template #nav>
          <Phasebar
            @updateProgress="updateProgress"
            @updateCurrentPhase="updateCurrentPhase"
          />
        </template>
        <template #progressBar>
          <ProgressBar :progress="progress" />
        </template>
        <template #addressBar>
          <TransitionNavbar>
            <AddressBar
              v-show="showAddressBar"
              :native-back-function="orderFlowSignup"
              :label="addressBarLabel"
              :secure="currentPhase.secure"
              :show-back-button="currentPhase.showBackButton"
              :show-confirmation="!!currentPhase.showConfirmation"
            />
          </TransitionNavbar>
        </template>
      </Navbar>
    </TransitionNavbar>
    <router-view
      v-slot="{ Component, route }"
      :should-scroll="contentVisible"
      @updateAddressBar="updateAddressBar"
      @outclick="disableDynamicTitle"
    >
      <TransitionComparerView
        @beforeLeave="setScrollPosition"
        @afterLeave="resetNavAnimation"
        @enter="animateNavbar"
        @afterEnter="showContent"
      >
        <component
          :is="Component"
          :key="route.path"
          v-model:orderFlowSignup="orderFlowSignup"
        />
      </TransitionComparerView>
    </router-view>
  </div>
</template>

<script setup>
import {
    onMounted, watch, ref, computed, onUpdated,
} from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { useWindowBlurTitle } from 'vue-elements/composables/comparison.js';
import { tryResumeChat } from '../../../assets/js/helpers/chat';
import Navbar from '../../vue-elements/navbar/Navbar.vue';
import TransitionNavbar from '../../vue-elements/transitions/TransitionNavbar.vue';
import ProgressBar from '../../../abstracts/elements/progressBar/ProgressBar.vue';
import AddressBar from './components/AddressBar.vue';
import Phasebar from './components/Phasebar.vue';
import TransitionComparerView from '../../vue-elements/transitions/TransitionComparerView.vue';
import ButtonNoIcon from '../../../abstracts/elements/button/no-icon/button-no-icon.vue';
import { useNavbarTransition } from '../../vue-elements/composables/transition.js';

const overwriteAddressLabel = ref('');
const themeName = ref('theme-energy-price-focus');
const dynamicTitleEnabled = ref(true);
const progress = ref(0);
const currentPhase = ref({});
const pageTimeout = ref(null);
const router = useRouter();
const orderFlowSignup = ref(false);

// Confirm leaving page
const currentRoute = computed(() => router.currentRoute.value.name);
window.onbeforeunload = () => (currentRoute.value === 'form' ? 'Weet je zeker dat je de pagina wilt verlaten?' : null);

// Navbar Transition
const {
    showNavbar,
    showAddressBar,
    contentVisible,
    resetNavAnimation,
    animateNavbar,
    setScrollPosition,
    showContent,
} = useNavbarTransition();

onMounted(() => {
    // Check if user was chatting on hard refresh
    tryResumeChat();
});

useWindowBlurTitle(dynamicTitleEnabled);
const logoClick = () => {
    window.location.replace('/');
};

const updateAddressBar = (newLabel) => {
    overwriteAddressLabel.value = newLabel;
};

const updateProgress = (updatedProgress) => {
    progress.value = updatedProgress;
};

const updateCurrentPhase = (newPhase) => {
    overwriteAddressLabel.value = '';
    currentPhase.value = newPhase;
};

const disableDynamicTitle = () => {
    dynamicTitleEnabled.value = false;
};

// Transition stuff

const addressBarLabel = computed(() => {
    if (overwriteAddressLabel.value) {
        return overwriteAddressLabel.value;
    }
    return currentPhase.value.title || 'Energie vergelijken';
});

watch(currentPhase, () => {
    document.title = currentPhase.value.pageTitle;
});

</script>

<style lang="scss">
@use 'assets/scss/all.scss' as *;

#energyComparison {
  @include breakpoint-up(xl) {
    @include background-light-blue;
  }
}


.comparison-module.confirmation h1 {
  font-size: 32px;
}
</style>
